import {
    DimensionConfig,
    MetricsConfig,
    ChartColorConfig,
    METRIC_API_TYPE,
    COMPARE_PERIODS,
    DIMENSION_SEPARATOR,
    UNKNOWN_DIMENSION,
    NA_VALUE,
    FULL_CHANGE_PCT,
    TIME_BREAKDOWN_ALL,
} from './consts';
import { buildFilter, buildMetricParams } from '../utils/reportQueryUtils';

const periodKey = (key, period) => `${key}__${period}`;

export const getMetricColumnKey = metricName => {
    const config = MetricsConfig[metricName];
    if (!config) return null;

    const { apiName, apiType, period } = config;
    return apiType === METRIC_API_TYPE.COHORT ? periodKey(apiName, period) : apiName;
};

const resolveValue = (row, key) => {
    const val = row[key];
    if (val == null || val === NA_VALUE) return null;
    if (typeof val === 'number') return Number.isNaN(val) ? null : val;
    if (typeof val === 'object' && val.fields) {
        const [, numerator] = val.fields[0];
        const [, denominator] = val.fields[1];
        return denominator === 0 ? null : numerator / denominator;
    }
    return null;
};

const splitBySign = values => {
    const negatives = [];
    const positives = [];
    values.forEach((val, idx) => {
        if (val == null) return;
        if (val < 0) negatives.push({ val, idx });
        else if (val > 0) positives.push({ val, idx });
    });
    negatives.sort((a, b) => a.val - b.val);
    positives.sort((a, b) => a.val - b.val);
    return { negatives, positives };
};

const assignRanks = (entries, toNormalized) => {
    let rank = 0;
    entries.forEach((entry, i) => {
        if (i > 0 && entry.val !== entries[i - 1].val) rank = i;
        toNormalized(entry.idx, rank);
    });
};

const rankNormalize = values => {
    const normalized = values.map(val => (val == null ? null : 0));
    const { negatives, positives } = splitBySign(values);

    assignRanks(negatives, (idx, rank) => {
        normalized[idx] = -(negatives.length - rank) / negatives.length;
    });
    assignRanks(positives, (idx, rank) => {
        normalized[idx] = (rank + 1) / positives.length;
    });

    return normalized;
};

const hexToRgb = hex => [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];

const rgbToHex = ([r, g, b]) =>
    `#${((1 << 24) + (r << 16) + (g << 8) + b)
        .toString(16)
        .slice(1)
        .toLowerCase()}`;

const interpolateColor = (from, to, ratio) => rgbToHex(from.map((c, i) => Math.round(c + (to[i] - c) * ratio)));

const GOOD_RGB = hexToRgb(ChartColorConfig.GOOD);
const BAD_RGB = hexToRgb(ChartColorConfig.BAD);
const NEUTRAL_RGB = hexToRgb(ChartColorConfig.NEUTRAL);

export const getPointColor = (normalized, increasesAreGood) => {
    if (normalized == null || !Number.isFinite(normalized)) return ChartColorConfig.NO_DATA;
    if (normalized === 0) return ChartColorConfig.NEUTRAL;

    const positiveRgb = increasesAreGood ? GOOD_RGB : BAD_RGB;
    const negativeRgb = increasesAreGood ? BAD_RGB : GOOD_RGB;

    if (normalized > 0) return interpolateColor(NEUTRAL_RGB, positiveRgb, normalized);
    return interpolateColor(NEUTRAL_RGB, negativeRgb, Math.abs(normalized));
};

const computeColorChange = (row, p1Key, p2Key) => {
    const p1 = resolveValue(row, p1Key);
    const p2 = resolveValue(row, p2Key);
    if (p1 == null || p2 == null) return { abs: null, pct: null };
    const abs = p1 - p2;
    const pct = p2 !== 0 ? ((p1 - p2) / Math.abs(p2)) * FULL_CHANGE_PCT : p1 !== 0 ? FULL_CHANGE_PCT : 0;
    return { abs, pct };
};

const buildPointId = (row, dimensionKeys) => {
    const dimValues = dimensionKeys.map(k => (row[k] != null ? String(row[k]) : UNKNOWN_DIMENSION));
    return {
        id: dimValues.join(DIMENSION_SEPARATOR),
        parent: dimValues.slice(0, -1).join(DIMENSION_SEPARATOR),
        name: dimValues[dimValues.length - 1],
    };
};

export const buildRingPoints = (rows, dimensionKeys, sizeKey, colorKey, ringLevel, increasesAreGood = true) => {
    const sizeP1Key = periodKey(sizeKey, COMPARE_PERIODS.CURRENT);
    const sizeP2Key = periodKey(sizeKey, COMPARE_PERIODS.PREVIOUS);
    const colorP1Key = periodKey(colorKey, COMPARE_PERIODS.CURRENT);
    const colorP2Key = periodKey(colorKey, COMPARE_PERIODS.PREVIOUS);

    const changes = rows.map(row => computeColorChange(row, colorP1Key, colorP2Key));
    const normalizedValues = rankNormalize(changes.map(change => change.pct));

    return rows
        .map((row, i) => {
            const sizeP1 = resolveValue(row, sizeP1Key) ?? 0;
            const sizeP2 = resolveValue(row, sizeP2Key) ?? 0;
            const normalized = normalizedValues[i];
            const isNoData = normalized == null;
            const color = isNoData ? ChartColorConfig.NO_DATA : getPointColor(normalized, increasesAreGood);

            return {
                ...buildPointId(row, dimensionKeys),
                value: sizeP1,
                color,
                custom: {
                    rawColorChange: changes[i].abs,
                    rawColorChangePct: changes[i].pct,
                    rawSizeChange: sizeP1 - sizeP2,
                    ringLevel,
                    originalColor: color,
                },
            };
        })
        .filter(point => point.value > 0);
};

export const computeSubtreeColors = (chart, rootId, increasesAreGood) => {
    const { points } = chart.series[0];
    const subtreePrefix = `${rootId}${DIMENSION_SEPARATOR}`;
    const subtreePoints = points.filter(p => p.id === rootId || p.id.startsWith(subtreePrefix));

    const pointsByRing = new Map();
    subtreePoints.forEach(point => {
        const ringLevel = point.custom?.ringLevel;
        const bucket = pointsByRing.get(ringLevel) || [];
        bucket.push(point);
        pointsByRing.set(ringLevel, bucket);
    });

    const updates = [];
    pointsByRing.forEach(ringPoints => {
        const pcts = ringPoints.map(p => p.custom?.rawColorChangePct ?? null);
        const normalized = rankNormalize(pcts);
        ringPoints.forEach((point, i) => {
            const value = normalized[i];
            const color = value == null ? ChartColorConfig.NO_DATA : getPointColor(value, increasesAreGood);
            updates.push({ point, color });
        });
    });

    return updates;
};

export const restoreOriginalColors = chart =>
    chart.series[0].points.map(point => ({ point, color: point.custom?.originalColor }));

export const buildRingQueryParams = ({ dimensions, filters, firstPeriod, secondPeriod }) => {
    const apiFilters = filters
        .filter(f => f.values?.length)
        .map(filter =>
            buildFilter(
                DimensionConfig[filter.type].apiName,
                filter.values.map(value => value.value)
            )
        );

    const baseParams = {
        ...buildMetricParams(Object.values(MetricsConfig)),
        filters: apiFilters,
        compare: true,
        start_date: firstPeriod.start,
        end_date: firstPeriod.end,
        start_date_2: secondPeriod.start,
        end_date_2: secondPeriod.end,
        time_breakdown: [TIME_BREAKDOWN_ALL],
    };

    return dimensions.map((_, index) => ({
        ...baseParams,
        dimensions: dimensions.slice(0, index + 1).map(d => DimensionConfig[d.value].apiName),
    }));
};

export const buildQueryKey = ({ dimensions, filters, firstPeriod, secondPeriod }) =>
    JSON.stringify({
        dimensions: dimensions.map(d => d.value),
        filters: filters
            .map(f => ({ type: f.type, values: (f.values || []).map(v => v.value).sort() }))
            .sort((a, b) => a.type.localeCompare(b.type)),
        firstPeriod,
        secondPeriod,
    });
